import React, { useMemo } from 'react'
import {
  Box,
  Checkbox,
  FormControlLabel,
  FormHelperText,
  makeStyles,
} from '@material-ui/core'
import { useTranslation } from 'react-i18next'
import { intersection, uniq, sortBy } from 'lodash'
import { groupBy } from '../../util/array-utility'
import { EnergyType } from '../../calculators/EnergyFlow'

const useStyles = makeStyles((theme) => ({
  picker: {
    paddingTop: theme.spacing(1),
    paddingBottom: theme.spacing(1),
    paddingLeft: theme.spacing(2),
    paddingRight: theme.spacing(2),
    height: 370,
    borderStyle: 'solid',
    borderWidth: 1,
    borderColor: theme.palette.grey['400'],
    borderRadius: theme.shape.borderRadius,
    overflow: 'scroll',
  },
}))

export type PickerSubCollection = {
  id: string
  name: string
  type: EnergyType
}

export type PickerCollection = {
  id: string
  name: string
  subCollections: PickerSubCollection[]
}

export type MultiPickerProps = {
  collections: PickerCollection[]
  name: string
  value: string[]
  error?: string
  onChange: (value: string[]) => void
}

export function MultiPicker({
  collections,
  name,
  value,
  error,
  onChange,
}: MultiPickerProps): React.ReactElement {
  const classes = useStyles()
  const { t } = useTranslation()

  const isEverythingSelected = (children: string[]): boolean =>
    intersection(value, children).length === children.length

  const isPartiallySelected = (children: string[]): boolean => {
    const matchingChildCount = intersection(value, children).length

    return matchingChildCount > 0 && matchingChildCount < children.length
  }

  const allSubCollectionIds = useMemo(
    () =>
      collections
        .flatMap((collection) => collection.subCollections)
        .map((subCollection) => subCollection.id),
    [collections],
  )

  const toggleValue = (toggledValues: string[]) => {
    const everyValueSelected = isEverythingSelected(toggledValues)

    if (everyValueSelected) {
      onChange(value.filter((v) => !toggledValues.includes(v)))
    } else {
      onChange(
        sortBy(uniq([...value, ...toggledValues]), (v) =>
          allSubCollectionIds.indexOf(v),
        ),
      )
    }
  }

  return (
    <Box id={`multi-picker-${name}`}>
      <Box className={classes.picker}>
        {collections.map((collection) => {
          const filteredHourSeries = collection.subCollections

          if (filteredHourSeries.length === 0) {
            return <div key={collection.id} />
          }

          const collectionHourSeriesByType = groupBy(
            filteredHourSeries,
            (hourSeries) => hourSeries.type,
          )
          const collectionHourSeriesIds = filteredHourSeries.map(
            (hourSeries) => hourSeries.id,
          )

          return (
            <Box key={collection.id}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={isEverythingSelected(collectionHourSeriesIds)}
                    indeterminate={isPartiallySelected(collectionHourSeriesIds)}
                    onChange={() => toggleValue(collectionHourSeriesIds)}
                  />
                }
                label={collection.name}
              />

              <Box pl={4}>
                {Object.entries(collectionHourSeriesByType).map(
                  ([type, hourSeriesList]) => {
                    const typeHourSeriesIds = hourSeriesList.map(
                      (hourSeries) => hourSeries.id,
                    )

                    return (
                      <Box key={`${collection.id}-${type}`}>
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={isEverythingSelected(typeHourSeriesIds)}
                              indeterminate={isPartiallySelected(
                                typeHourSeriesIds,
                              )}
                              onChange={() => toggleValue(typeHourSeriesIds)}
                            />
                          }
                          label={t(`energyType.${type}`)}
                        />

                        <Box pl={4}>
                          {hourSeriesList.map((hourSeries) => (
                            <Box key={hourSeries.id}>
                              <FormControlLabel
                                control={
                                  <Checkbox
                                    checked={value.includes(hourSeries.id)}
                                    onChange={() =>
                                      toggleValue([hourSeries.id])
                                    }
                                  />
                                }
                                label={hourSeries.name}
                              />
                            </Box>
                          ))}
                        </Box>
                      </Box>
                    )
                  },
                )}
              </Box>
            </Box>
          )
        })}
      </Box>

      {error && <FormHelperText error>{error}</FormHelperText>}
    </Box>
  )
}
